/**
 * Organization Context
 *
 * Manages the selected organization.
 * Super admins can switch between organizations to create sessions/prompts as different orgs;
 * everyone else, org admins included, is pinned to their own organization.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { usePolicy } from './PolicyContext';
import axios from 'axios';
import { getBackendUrl } from '../utils/utils';

interface Organization {
  id: string;
  name: string;
  location: string;
}

interface OrganizationContextType {
  selectedOrganizationId: string | null;
  setSelectedOrganizationId: (id: string | null) => void;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const noOpOrganizationContext: OrganizationContextType = {
  selectedOrganizationId: null,
  setSelectedOrganizationId: () => {},
  organizations: [],
  loading: false,
  error: null,
  refreshOrganizations: async () => {},
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  return context ?? noOpOrganizationContext;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { userRole, userData, token } = useAuth();
  const { policiesAccepted } = usePolicy();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user's organization ID
  const userOrganizationId = userData?.organization_id || userData?.organization?.id;

  // Initialize selected organization from user data or localStorage
  useEffect(() => {
    if (userRole === 'super_admin') {
      // Super admins work across orgs: restore their last pick, or null (platform-wide)
      const savedOrgId = localStorage.getItem('selected_organization_id');
      setSelectedOrganizationId(savedOrgId);
    } else {
      // Everyone else, org admins included, is pinned to their own organization. The
      // backend scopes them to it regardless, and nothing in the UI lets them pick
      // another, so null or a stale saved id here hid their org's templates and blocks
      // from Prompt Studio, which only offers what matches the selected org.
      setSelectedOrganizationId(userOrganizationId ?? null);
    }
  }, [userRole, userOrganizationId]);

  // Fetch organizations list (for admin and super_admin users)
  const refreshOrganizations = async () => {
    if (userRole !== 'admin' && userRole !== 'super_admin' || !token) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const allOrgs: Organization[] = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const response = await axios.get(`${getBackendUrl()}/organization`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, page_size: 100 },
        });
        allOrgs.push(...(response.data.items || []));
        hasNext = response.data.pagination_info?.has_next ?? false;
        page += 1;
      }
      setOrganizations(allOrgs);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organizations when admin or super_admin user logs in.
  // Gated on policiesAccepted so we don't fire /organization while the policy-acceptance modal is blocking the app.
  useEffect(() => {
    if ((userRole === 'admin' || userRole === 'super_admin') && token && policiesAccepted === true) {
      refreshOrganizations();
    }
  }, [userRole, token, policiesAccepted]);

  // Custom setter that persists to localStorage. Only a super admin can switch orgs;
  // for anyone else it is a no-op, so the selection stays on their own organization.
  const setSelectedOrgWithPersistence = (id: string | null) => {
    if (userRole !== 'super_admin') return;
    setSelectedOrganizationId(id);
    if (id) {
      localStorage.setItem('selected_organization_id', id);
    } else {
      localStorage.removeItem('selected_organization_id');
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        selectedOrganizationId,
        setSelectedOrganizationId: setSelectedOrgWithPersistence,
        organizations,
        loading,
        error,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
